import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, ArrowRight, User, Phone, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/hola/PasswordInput";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdvisorAuthPage() {
  const nav = useNavigate();
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [form, setForm] = useState({ full_name: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const path = mode === "register" ? "/api/advisors/register" : "/api/advisors/login";
    const body = mode === "register"
      ? { full_name: form.full_name.trim(), phone: form.phone, password: form.password }
      : { phone: form.phone, password: form.password };
    if (mode === "register" && form.full_name.trim().length < 3) {
      toast.error("Escribe tu nombre completo");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}${path}`, body);
      localStorage.setItem("holaradar_advisor_token", data.token);
      localStorage.setItem("holaradar_advisor", JSON.stringify(data.advisor));
      toast.success(mode === "register" ? "¡Bienvenido!" : "Sesión iniciada");
      nav("/asesor");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pude autenticarte");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B1120] via-neutral-950 to-black text-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-block w-14 h-14 rounded-2xl bg-gradient-to-br from-[#0055FF] to-[#FF007A] grid place-items-center mb-3 shadow-xl">
            <Briefcase className="w-7 h-7" />
          </div>
          <h1 className="font-heading font-extrabold text-3xl">Área Asesores</h1>
          <p className="text-sm text-neutral-400 mt-1">Registra tus ventas y ve tus comercios en un lugar</p>
        </div>

        <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6">
          <div className="flex mb-5 rounded-full bg-white/5 p-1">
            <button
              type="button"
              onClick={() => setMode("login")}
              className={`flex-1 py-2 rounded-full text-sm font-bold transition-colors ${mode === "login" ? "bg-[#0055FF] text-white" : "text-neutral-400"}`}
              data-testid="advisor-tab-login"
            >
              Iniciar sesión
            </button>
            <button
              type="button"
              onClick={() => setMode("register")}
              className={`flex-1 py-2 rounded-full text-sm font-bold transition-colors ${mode === "register" ? "bg-[#0055FF] text-white" : "text-neutral-400"}`}
              data-testid="advisor-tab-register"
            >
              Registrarme
            </button>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label className="text-xs text-neutral-300 flex items-center gap-2"><User className="w-3 h-3" /> Nombre completo</Label>
                <Input
                  value={form.full_name}
                  onChange={(e) => upd("full_name", e.target.value)}
                  placeholder="Ej: Ana Martínez"
                  className="mt-1 h-11 rounded-xl bg-white/10 border-white/20 text-white placeholder:text-neutral-500"
                  maxLength={120}
                  autoFocus
                  data-testid="advisor-fullname-input"
                />
              </div>
            )}
            <div>
              <Label className="text-xs text-neutral-300 flex items-center gap-2"><Phone className="w-3 h-3" /> Celular (10 dígitos)</Label>
              <Input
                type="tel"
                value={form.phone}
                onChange={(e) => upd("phone", e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="5512345678"
                className="mt-1 h-11 rounded-xl bg-white/10 border-white/20 text-white placeholder:text-neutral-500"
                data-testid="advisor-phone-input"
                autoFocus={mode === "login"}
              />
            </div>
            <div>
              <Label className="text-xs text-neutral-300 flex items-center gap-2"><Lock className="w-3 h-3" /> Contraseña</Label>
              <PasswordInput
                value={form.password}
                onChange={(e) => upd("password", e.target.value)}
                placeholder={mode === "register" ? "Mínimo 6 caracteres" : "Tu contraseña"}
                className="mt-1 h-11 rounded-xl bg-white/10 border-white/20 text-white"
                testid="advisor-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-full bg-[#0055FF] hover:bg-[#0044cc] font-bold"
              data-testid="advisor-submit"
            >
              {loading ? "Enviando…" : (mode === "register" ? "Crear cuenta" : "Entrar")}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
